import { useMemo, useEffect, useState } from 'react'
import DateInput from './DateInput'
import MultiSelect from './MultiSelect'
import { exportExcel } from '@/utils/spreadsheetHelpers'
import { ColumnDataType, DEVICE_EXPORT_TEMPLATE, RequiredColumn } from '@/constants/templateSchema'
import { authService, PermissionScope } from '@/services/AuthService'
import { RESOURCES } from '@/constants/resources'
import { CONFIG_STATUS_MAP, FIRMWARE_STATUS_MAP } from './DeviceTableRow'
import { Configuration, PartnerConfigurationService } from '@/services/PartnerConfigurationService'
import { Device } from '@/models/device'
import { usePartner } from '@/contexts/partnerContext'
import { CustomField } from '@/models/customField'

interface DeviceFilters {
  serialNumber: string
  imei: string
  phoneNumber: string
  simSerialNumber: string
  simProvider: string[]
  customFields: Record<string, string[]>
  type: string[]
  configurationId: string[]
  firmware: string[]
  configurationStatus: string[],
  firmwareStatus: string[],
  dateAddedFrom: string
  dateAddedTo: string
}

interface DeviceFiltersProps {
  filters: DeviceFilters
  onFilterChange: (key: keyof DeviceFilters, value: string | string[]) => void
  onCustomFieldFilterChange: (fieldId: string, values: string[]) => void
  onClearAllFilters: () => void
  devices: Device[]
  customFields: CustomField[]
  filteredDevices: Device[]
}

export default function DeviceFiltersComponent({
  filters,
  onFilterChange,
  onCustomFieldFilterChange,
  onClearAllFilters,
  devices,
  customFields,
  filteredDevices
}: DeviceFiltersProps) {
  const [configurations, setConfigurations] = useState<Configuration[]>([])
  const [loadingConfigurations, setLoadingConfigurations] = useState(false)

  const { impersonatedPartnerId } = usePartner();
  const shouldGetPartnerFilters = impersonatedPartnerId !== null || authService.channelPartnerId !== null;
  
  const hasCustomFieldsPermission = authService.hasPermission(RESOURCES.CUSTOM_FIELDS, PermissionScope.READ);

  // Load configurations on mount
  useEffect(() => {
    const loadConfigurations = async () => {
      if (!shouldGetPartnerFilters) {
        setLoadingConfigurations(false)
        return;
      }

      if (!shouldGetPartnerFilters) {
        setLoadingConfigurations(false)
        return;
      }

      setLoadingConfigurations(true)
      try {
        const configs = (await PartnerConfigurationService.getAll()).configurations
        if (configs === null) {
          setConfigurations([])
          return;
        }
        
        setConfigurations(configs)
      } catch (error) {
        // Silently handle error - user might not be authenticated yet
        setConfigurations([])
      } finally {
        setLoadingConfigurations(false)
      }
    }
    if (authService.hasPermission(RESOURCES.PARTNER_CONFIG, PermissionScope.READ)) {
      loadConfigurations()
    }
  }, [])

  // Get unique values for dropdown filters
  const uniqueValues = useMemo(() => {
    return {
      simProviders: [...new Set(devices.map(d => d.simProviderName).filter(Boolean))],
      deviceTypes: [...new Set(devices.map(d => d.type).filter(Boolean))],
      configurationIds: [...new Set(devices.map(d => d.configurationId).filter(Boolean))],
      firmwares: [...new Set(devices.map(d => d.firmware).filter(Boolean))],
      configurationStatuses: Object.entries(CONFIG_STATUS_MAP).map(([key, value]) => ({
        key: key,
        label: value.label
      })),
      firmwareStatuses: Object.entries(FIRMWARE_STATUS_MAP).map(([key, value]) => ({
        key: key,
        label: value.label
      }))
    }
  }, [devices])

  const activeFiltersCount = useMemo(() => {
    let count = 0;
  
    Object.entries(filters).forEach(([key, value]) => {
      if (key === 'customFields' && value && typeof value === 'object' && !Array.isArray(value)) {
        const activeCustomFieldCount = Object.values(value).filter(
          (selections) => Array.isArray(selections) && selections.length > 0
        ).length;
        
        count += activeCustomFieldCount;
      } 
      else if (Array.isArray(value)) {
        if (value.length > 0) count++;
      } 
      else {
        if (value !== '' && value !== null && value !== undefined) count++;
      }
    });
  
    return count;
  }, [filters]);

  const exportToExcel = async () => {
      const partnerCustomFields = customFields.map(customField => ({
        id: customField.id, 
        name: customField.name 
      }));

      const dynamicColumns: RequiredColumn[] = partnerCustomFields.map(partnerCustomField => ({
        header: partnerCustomField.name,
        forceTextFormat: true,
        type: ColumnDataType.String 
    }));

      const columns = [...DEVICE_EXPORT_TEMPLATE.columns, ...dynamicColumns];

      const dataRows = filteredDevices.map((device) => {
        const staticFields = [
          device.serialNumber ? device.serialNumber : '',
          device.phoneNumber ? device.phoneNumber : '',
          device.simProviderName || '',
          device.serialNumber || '',
          device.imei ? device.imei : '',
          device.type || '',
          device.dateAddedUtc ? device.dateAddedUtc : '',
          device.configurationName || '',
          device.configurationStatus || '',
          device.firmware || '',
          device.firmwareStatus || '',
        ];

        const dynamicFields = partnerCustomFields.map(partnerCustomField => {
          const matchingFields = device.subCustomFields?.filter(
            subCustomField => subCustomField.parentCustomFieldId === partnerCustomField.id
          ) || [];

          const commaSeparatedNames = matchingFields
            .map(field => field.name)
            .join(', ');  

          return commaSeparatedNames;
      });

      return [...staticFields, ...dynamicFields];
    });

    exportExcel(DEVICE_EXPORT_TEMPLATE.name, columns, dataRows);
  }

  return (
    <div className="device-filters">
      <h3>
        Filter Devices
        {activeFiltersCount > 0 && (
          <span className="active-filters-count">{activeFiltersCount}</span>
        )}
      </h3>

      <div className="filters-grid">
        {/* Text-based filters */}
        <div className="filter-group">
          <label>Device Serial Number</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by serial number..."
            value={filters.serialNumber}
            onChange={(e) => onFilterChange('serialNumber', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>IMEI Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by IMEI..."
            value={filters.imei}
            onChange={(e) => onFilterChange('imei', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>Phone Number Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by phone number..."
            value={filters.phoneNumber}
            onChange={(e) => onFilterChange('phoneNumber', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>SIM Serial Search</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search by SIM serial..."
            value={filters.simSerialNumber}
            onChange={(e) => onFilterChange('simSerialNumber', e.target.value)}
          />
        </div>

        {/* Dropdown filters */}
        <div className="filter-group">
          <label>SIM Provider</label>
          <MultiSelect
            options={uniqueValues.simProviders
                .filter((provider): provider is string => !!provider)
                .map(provider => ({
                  value: provider,
                  label: provider
                }))}
            selectedValues={filters.simProvider}
            onChange={(values) => onFilterChange('simProvider', values)}
            placeholder="All Providers"
            className="filter-select"
          />
        </div>

        {hasCustomFieldsPermission && customFields.sort(cf => cf.slot).map((field) => (
          <div className="filter-group" key={field.id}>
            <label>{field.name}</label>
            <MultiSelect
              options={field.subFields?.map(sub => ({
                value: sub.name, 
                label: sub.name
              })) ?? []}
              selectedValues={filters.customFields?.[field.id] || []}
              onChange={(newValues) => onCustomFieldFilterChange(field.id, newValues)}
              placeholder="Custom Fields"
              className="filter-select"
            />
          </div>
        ))}

        <div className="filter-group">
          <label>Device Type</label>
          <MultiSelect
            options={uniqueValues.deviceTypes
                .filter((provider): provider is string => !!provider)
                .map(type => ({
                  value: type,
                  label: type
                }))}
            selectedValues={filters.type}
            onChange={(values) => onFilterChange('type', values)}
            placeholder="All Device Types"
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Current Configuration</label>
          <MultiSelect
            options={configurations.map(config => ({
              value: config.id,
              label: config.name
            }))}
            selectedValues={filters.configurationId}
            onChange={(values) => onFilterChange('configurationId', values)}
            placeholder={loadingConfigurations ? 'Loading configurations...' : 'All Configurations'}
            disabled={loadingConfigurations}
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Current Firmware</label>
          <MultiSelect
            options={uniqueValues.firmwares
                .filter((provider): provider is string => !!provider)
                .map(firmware => ({
                  value: firmware,
                  label: firmware
                }))}
            selectedValues={filters.firmware}
            onChange={(values) => onFilterChange('firmware', values)}
            placeholder="All Firmware"
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Configuration Status</label>
          <MultiSelect
            options={uniqueValues.configurationStatuses.map(status => ({
              value: status.key,
              label: status.label
            }))}
            selectedValues={filters.configurationStatus}
            onChange={(values) => onFilterChange('configurationStatus', values)}
            placeholder="All Statuses"
            className="filter-select"
          />
        </div>

        <div className="filter-group">
          <label>Firmware Status</label>
          <MultiSelect
            options={uniqueValues.firmwareStatuses.map(status => ({
              value: status.key,
              label: status.label
            }))}
            selectedValues={filters.firmwareStatus}
            onChange={(values) => onFilterChange('firmwareStatus', values)}
            placeholder="All Statuses"
            className="filter-select"
          />
        </div>

        {/* Date filters */}
        <div className="filter-group">
          <label>Date Added From</label>
          <DateInput
            value={filters.dateAddedFrom}
            onChange={(value) => onFilterChange('dateAddedFrom', value)}
            className="filter-input"
          />
        </div>

        <div className="filter-group">
          <label>Date Added To</label>
          <DateInput
            value={filters.dateAddedTo}
            onChange={(value) => onFilterChange('dateAddedTo', value)}
            className="filter-input"
          />
        </div>
      </div>

      <div className="filters-actions">
        <button
          className="clear-filters-btn"
          onClick={onClearAllFilters}
          disabled={activeFiltersCount === 0}
        >
          Clear All Filters
        </button>
      </div>

      <div className="export-section">
        <button
          className="export-data-btn"
          onClick={exportToExcel}
          disabled={filteredDevices.length === 0}
        >
          Export Data
        </button>
      </div>
    </div>
  )
}

export type { DeviceFilters }